import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';

import { AuthenticationError, AuthService } from '@auth0/auth0-angular';
import { isSegmentEnabled, version } from 'config';
import { AutoUnsubscribe } from 'helpers';
import { Subscription, combineLatest } from 'rxjs';
import { filter, switchMap, take, catchError } from 'rxjs/operators';
import { LanguageService, UserService } from 'services';
import { MonitoringService } from 'services/monitoring.service';
import { ObservableSpyService } from 'app/b2b-order/services/observable-spy.service';
import { logging } from 'helpers/monitoring';
import { AuthenticationModel } from './authentication/authentication.model';
import { AuthenticatedUserService } from 'services/authenticatedUser';

@Component({
    selector: 'fc-app',
    templateUrl: './app.component.html',
    standalone: false
})
@AutoUnsubscribe
export class AppComponent implements OnInit {
    public segmentEnabled: boolean;
    private subscription: Subscription = new Subscription();

    constructor(
        private languageService: LanguageService,
        private userService: UserService,
        private monitoringService: MonitoringService,
        private spyService: ObservableSpyService,
        private auth0Service: AuthService,
        private router: Router,
        private authenticationModel: AuthenticationModel,
        private authenticatedUserService: AuthenticatedUserService
    ) {}

    ngOnInit() {
        this.monitoringService.init();
        this.spyService.init();

        this.subscription.add(
            /**
             * Since the Auth0 library takes care of all query params related to auth0 errors,
             * we need to handle auth errors exclusively through the Auth0Service
             */
            this.auth0Service.error$.subscribe((error: any) => {
                // JIT migration flow, redirects user to the organization page the first time they login
                if (
                    error instanceof AuthenticationError &&
                    error.error === 'access_denied' &&
                    error.error_description === 'jit_migration'
                ) {
                    logging.info('JIT migration flow, refreshing session', null);
                    this.userService.removeUser();
                    this.auth0Service.loginWithRedirect();
                }

                if (error.error === 'invalid_grant' || error.error === 'missing_refresh_token') {
                    logging.info('Invalid grant or missing refresh token, refreshing session', null);
                    this.auth0Service.loginWithRedirect();
                }

                logging.error('Auth0 Error:', error);
            })
        );

        // Add subscription for router events to check user profile
        this.subscription.add(
            this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
                // Check only if user is considered logged in (token exists) but profile might be incomplete
                if (this.userService.isLoggedIn() && !this.userService.isUserProfileLoaded()) {
                    logging.info('AppComponent: User token exists but profile incomplete. Attempting refresh.', null);
                    this.refreshUserProfile();
                }
            })
        );

        const preferredLanguage = this.userService.getPreferredLanguage();
        this.languageService.use(preferredLanguage ? preferredLanguage : this.languageService.getCurrentLanguage());

        logging.info(`Welcome to the FASHION CLOUD Web Application v${version}`, null);
        this.segmentEnabled = isSegmentEnabled();
    }

    private refreshUserProfile(): void {
        // This logic is adapted from the userGuard and ensures we only attempt refresh if an Auth0 user session exists.
        this.subscription.add(
            this.auth0Service.user$
                .pipe(
                    take(1), // Take the current Auth0 user state once
                    filter((auth0User) => !!auth0User && !!auth0User.sub), // Proceed only if there's an Auth0 user with a sub
                    switchMap((auth0User) =>
                        combineLatest([
                            this.authenticationModel.getEnhancedSigninInfo(),
                            this.authenticationModel.getAppUserOrganizations(auth0User.sub) // Use sub from the emitted Auth0 user
                        ])
                    ),
                    take(1), // Ensure combineLatest and subsequent operations run once per trigger
                    catchError((error) => {
                        logging.error('AppComponent: Error during user profile refresh in refreshUserProfile.', error);
                        // Potentially redirect to login or show an error, but avoid getting into a loop.
                        // For now, just log and let other mechanisms handle critical auth failures.
                        return []; // Complete the observable chain to prevent breaking router event subscription.
                    })
                )
                .subscribe(([response, organizations]) => {
                    if (response && organizations) {
                        const userInfo = this.userService.prepareEnhancedSigninInfo(response);
                        this.authenticatedUserService.authenticate(userInfo);
                        this.authenticatedUserService.setUserOrganizations(organizations);
                        logging.info('AppComponent: User profile refreshed successfully.', null);
                    } else {
                        logging.warn(
                            'AppComponent: Profile refresh called but received incomplete data from services.',
                            { response, organizations }
                        );
                    }
                })
        );
    }

    get isLoggedIn() {
        return this.userService.isLoggedIn();
    }
}
